import type { BuilderBlock, BuilderPage, CmsPage } from '../../types'
import {
  requestAuthed,
  resolveTokenFromAuthHeaders,
  toErrorMessage,
  type LegacyAuthHeaders,
} from '../../lib/api/http'

type Translator = (key: string, params?: Record<string, string | number>) => string

const toObjectRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {}

const toChildrenArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
    : []

export const generateBuilderBlockId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `blk-${Date.now()}-${Math.round(Math.random() * 1_000_000)}`
}

export const normalizeBuilderBlock = (block: Record<string, unknown>): BuilderBlock => {
  const rawId = (typeof block.id === 'string' && block.id) || (typeof block._id === 'string' && block._id)
  const id = rawId && rawId.length > 0 ? rawId : generateBuilderBlockId()

  const type = typeof block.type === 'string' && block.type.length > 0 ? block.type : 'unknown'
  const props =
    block.props && typeof block.props === 'object' && !Array.isArray(block.props)
      ? (block.props as Record<string, unknown>)
      : {}
  const children = toChildrenArray(block.children).map((child) => normalizeBuilderBlock(child))

  return {
    id,
    type,
    props,
    children,
    visibility: block.visibility === 'hidden' || block.visibility === 'visible' ? block.visibility : undefined,
    anchorId: typeof block.anchorId === 'string' ? block.anchorId : undefined,
    customClassName: typeof block.customClassName === 'string' ? block.customClassName : undefined,
  }
}

const toPageContent = (raw: Record<string, unknown>): BuilderBlock[] => {
  const contentArray =
    (Array.isArray(raw.content) && raw.content) ||
    (Array.isArray(raw.layout) && raw.layout) ||
    (Array.isArray(raw.blocks) && raw.blocks) ||
    []

  return contentArray
    .map((entry) => (entry && typeof entry === 'object' ? normalizeBuilderBlock(entry as Record<string, unknown>) : null))
    .filter((entry): entry is BuilderBlock => Boolean(entry))
}

const toBuilderPage = (raw: Record<string, unknown>, fallbackPageId: string): BuilderPage => ({
  id: (typeof raw._id === 'string' && raw._id) || (typeof raw.id === 'string' && raw.id) || fallbackPageId,
  title: String(raw.title ?? ''),
  slug: String(raw.slug ?? '').replace(/^\/+/, ''),
  isSystem: Boolean(raw.isSystem),
  status: (raw.status as CmsPage['status']) ?? 'draft',
  content: toPageContent(raw),
  seo: raw.seo as BuilderPage['seo'],
})

export const requestBuilderPageDetail = async ({
  authHeaders,
  pageId,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  pageId: string
  t: Translator
}): Promise<BuilderPage> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const { response, payload } = await requestAuthed({
    token,
    path: `/admin/pages/${encodeURIComponent(pageId)}`,
  })
  if (!response.ok) {
    throw new Error(t('builder.toast.pageNotFound'))
  }

  const data = toObjectRecord(payload)
  return toBuilderPage(toObjectRecord(data.page), pageId)
}

export const resolveBuilderPathKey = (page: BuilderPage) => {
  const safeId = typeof page.id === 'string' ? page.id.trim() : ''
  const safeSlug = typeof page.slug === 'string' ? page.slug.trim().replace(/^\/+/, '') : ''
  const pathKey = /^[a-fA-F0-9]{24}$/.test(safeId) ? safeId : safeSlug || safeId
  return {
    pathKey,
    safeSlug,
  }
}

export const requestBuilderPageSave = async ({
  authHeaders,
  page,
  pathKey,
  safeSlug,
  t,
}: {
  authHeaders: LegacyAuthHeaders
  page: BuilderPage
  pathKey: string
  safeSlug: string
  t: Translator
}): Promise<{ content: BuilderBlock[]; slug: string }> => {
  const token = resolveTokenFromAuthHeaders(authHeaders)
  const { response, payload } = await requestAuthed({
    token,
    path: `/admin/pages/${encodeURIComponent(pathKey)}`,
    init: {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        title: page.title,
        slug: safeSlug || page.slug,
        content: page.content,
        seo: page.seo,
      }),
    },
  })

  if (!response.ok) {
    throw new Error(
      t('builder.toast.saveFailed', {
        status: response.status,
        message: toErrorMessage(payload, response.statusText),
      }),
    )
  }

  const data = toObjectRecord(payload)
  const rawPage = toObjectRecord(data.page)

  const content = Array.isArray(rawPage.content)
    ? rawPage.content
        .filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object')
        .map((entry) => normalizeBuilderBlock(entry))
    : page.content

  const slug = typeof rawPage.slug === 'string' ? rawPage.slug.replace(/^\/+/, '') : page.slug

  return { content, slug }
}
